import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme";
import { AnalyticsViewType } from "@intelligems/ig-types";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  ListItemButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { usePatchExperience } from "../../api/experiences/patch-experience";
import { setViewedAnalyticsDialog } from "../../store/slices/appSettings";
import { setIsTestOrdersOnly } from "../../store/slices/dashboardFilters";
import { isDialogOpen, setDialog } from "../../store/slices/global";
import { ExperienceFe } from "../../types/schemas/experiences";

import { analyticsViewTypesList } from "./Utils/AnalyticsViewTypeList";

const PREFIX = "AnalyticsViewTypeDialog";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
  articleLink: `${PREFIX}-articleLink`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.root}`]: {
    transition: "0.2s",
    borderRadius: "4px",
    justifyContent: "center",
    border: `1px solid ${theme.palette.gray.light}`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
  },
  [`& .${classes.dialogText1}`]: {
    marginBottom: "5px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    marginBottom: "20px",
    color: theme.palette.grey[900],
    fontSize: "14px",
  },
  [`& .${classes.articleLink}`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
}));

interface AnalyticsViewTypeDialogProps {
  experience: ExperienceFe;
}
export const AnalyticsViewTypeDialog: FC<AnalyticsViewTypeDialogProps> = ({
  experience,
}) => {
  const isOpen = useSelector(isDialogOpen("analyticsViewType"));
  const patchExperience = usePatchExperience(experience.id);
  const dispatch = useDispatch();

  const onSelect = (viewType: AnalyticsViewType) => {
    patchExperience.mutate({
      id: experience.id!,
      analyticsViewType: viewType,
    });
    dispatch(
      setIsTestOrdersOnly({
        value: viewType === "test_orders_only" || viewType === "none",
      })
    );

    dispatch(setDialog({ dialog: "analyticsViewType", open: false }));
    dispatch(setViewedAnalyticsDialog());
  };

  return (
    <StyledDialog
      open={isOpen}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          dispatch(setDialog({ dialog: "analyticsViewType", open: false }));
        }
      }}
      maxWidth={"xs"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <DialogTitle
          sx={{ width: "350px" }}
          id="dialogBox-title"
          className={classes.dialogTitle}
        >
          Select your Analytics View Type
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            className={classes.dialogText1}
            id="dialogBox-text-1"
          >
            Which orders would you like us to include in the results?
          </DialogContentText>
          <DialogContentText
            className={classes.dialogText2}
            id="dialogBox-text-1"
          >
            Please note that you can easily change this setting during / after
            your test in the filters menu within the test's analytics dashboard.
          </DialogContentText>
          <Grid container spacing={2} direction={"column"}>
            {analyticsViewTypesList.map((viewType) => (
              <Grid item xs={6} key={viewType.key}>
                <ListItemButton
                  classes={{ root: classes.root }}
                  onClick={() => {
                    onSelect(viewType.value as AnalyticsViewType);
                  }}
                >
                  <Grid container direction={"column"} alignItems={"center"}>
                    <Grid item>
                      <IgTypography variant="body1" sx={{ fontWeight: 600 }}>
                        {viewType.key}
                      </IgTypography>
                    </Grid>
                    <Grid item>
                      <IgTypography variant="body1">
                        {viewType.description}
                      </IgTypography>
                    </Grid>
                  </Grid>
                </ListItemButton>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Grid>
    </StyledDialog>
  );
};
